import React, { useCallback, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { colors, glyphs, useMediaQuery } from "../../../Styles";
import { NewSelect } from "../..";
import { field, FilterContent, NewFieldStyles } from "../../../Definitions";
import { getLabelFromKey, ReportData, ValueType } from "../../../Definitions/valueTypes";
import { createDisplayTypesFromOptions } from "../../../Definitions/filterTypes";
import { useFeedcurveOptions, useGroupOptions, useMilestoneOptions, useSectionOptions, useSystemOptions } from "../../_Components";

interface Lvl2_Props {
  reportData?: ReportData
  onReportDataChange: (data: ReportData) => void
}

export default function ReportModalLvl2({ reportData, onReportDataChange }:Lvl2_Props) {
  const {t} = useTranslation()

  const { feedcurveOptions } = useFeedcurveOptions();
  const { sectionOptions } = useSectionOptions();
  const groupOptions = useGroupOptions();
  const milestoneOptions = useMilestoneOptions();
  const systemOptions = useSystemOptions();

  const [selectFields, setSelectFields] = React.useState<field[]>([{ id: -1 }]);
    const mobileXs = useMediaQuery(`(max-width: 805px)`);
  
  // #region | Select Parameters
  const selectParameters = createDisplayTypesFromOptions({groupOptions, feedcurveOptions, milestoneOptions, systemOptions, sectionOptions});

  const filterParamOptions = selectParameters.map((type) => ({
    label: t(`params.${type.label}`, {defaultValue: type.label}), 
    id: type.id
  }));
  // #endregion

  // If reportData already have values to show
  useEffect(() => {
    if (reportData && reportData.filterParameter && reportData.filterParameter.length && reportData.filterParameter[0].length) {
      const updatedFields: field[] = reportData.filterParameter[0].map((param) => {
        const label = getLabelFromKey(param.key);
        const id = selectParameters.find((p) => p.label === label)?.id ?? -1;
        return { id, value: param.value };
      });
      updatedFields.push({ id: -1 });
      setSelectFields(updatedFields);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reportData?.filterParameter]);
  
  // Handle filter type change
  const handleFilterTypeChange = (index: number, id: number) => {
    const updatedSelectFields = [...selectFields]
    ;
    if (updatedSelectFields[index].id === -1) {
      updatedSelectFields.push({ id: -1 });
    }
    updatedSelectFields[index] = { id, value: undefined };

    const selectedParameter = selectParameters.find((param) => param.id === id);
    if (selectedParameter) {
      const updatedFilterParameter = reportData?.filterParameter?.[0] ? [...reportData.filterParameter[0]] : [];
      updatedFilterParameter[index] = {
        key: selectedParameter.key,
        value: []
      };
      onReportDataChange({...reportData, reportId: reportData?.reportId || 0, filterParameter: [updatedFilterParameter]});
    }
    setSelectFields(updatedSelectFields);
  };

  // Handle filter value change
  const handleFilterValueChange = (index: number, values: ValueType[]) => {
    const updatedFilterParameter = Array.isArray(reportData?.filterParameter?.[0]) ? [...reportData.filterParameter[0]] : [];
  
    if (!updatedFilterParameter[index]) return;
  
    updatedFilterParameter[index].value = values;
    onReportDataChange({...reportData, reportId: reportData?.reportId || 0, filterParameter: [updatedFilterParameter]});
  };

  // Remove a SelectField by index
  const handleRemoveField = useCallback((index: number) => {
    const updatedSelectFields = [...selectFields];
    updatedSelectFields.splice(index, 1);
    setSelectFields(updatedSelectFields);
  
    const updatedFilterParameter= reportData?.filterParameter?.[0];
    updatedFilterParameter?.splice(index, 1);
    onReportDataChange({...reportData, reportId: reportData?.reportId || 0, filterParameter: [(updatedFilterParameter ?? [])] });

  }, [onReportDataChange, reportData, selectFields]);

// #region | Rendered Fields
const renderedFields = selectFields.map((field, index) => {
  // Remove already used options from optionslist
  const selectedIds = selectFields
  .filter((f, i) => i !== index && f.id !== undefined)
  .map((f) => f.id); 
  const availableOptions = filterParamOptions.filter((option) => !selectedIds.includes(option.id));

  return (
    <div key={field.id !== undefined ? `${field.id}-${index}` : index} style={{ 
      width: "100%", 
      display: "flex", 
      flexDirection: "column", 
      gap: "10px", 
      marginBottom: index === selectFields.length - 1 ? "0px" : "30px"
    }}>
      <div style={{ display: "flex", flexDirection: "row", gap: "20px", alignItems: "end", justifyContent: "space-between" }}>
        <NewSelect
          placeholder={t("select", {defaultValue: "select"})}
          key={index+String(field.id)}
          options={availableOptions}
          onChange={(option) => {
            option?.id && handleFilterTypeChange(index,  Number(option.id));
          }}
          fieldOverrides={{
            fieldWidth: mobileXs ? "250px" : "280px",
            fieldStyle: NewFieldStyles.blueDarkSecondary
          }}
          valueOverrides={{
            defaultValue: filterParamOptions.find((parameter) => {return parameter.id === field.id})
          }}
        />
        {field.id !== -1 && ( <>
          <div style={{cursor: "pointer"}} onClick={() => handleRemoveField(index)}>
            <glyphs.Delete size="25px"/>
          </div>
        </> )}
      </div>
      {/* Content based on selected filter parameter */}
      {field.id !== -1 && (
        <div style={{    
          width: "100%",
          height: "fit-content",
          boxSizing: "border-box",
          backgroundColor: colors.greyLightSecondary,
          padding: mobileXs? "10px 5px" : "10px",
          borderRadius: "10px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}>
          <FilterContent 
            filter={field} 
            index={index} 
            filterParameterTypes={selectParameters} 
            handleFilterValueChange={(values) => {handleFilterValueChange(index, values)}}
          />
        </div>
      )}
    </div>
  )
})
// #endregion

  return (
    <div style={{
      display: "flex",
      alignItems: "center",
      justifyContent: "start",
      flexDirection: "column",
      boxSizing: "border-box"
    }}>
      {renderedFields}
    </div>
  )
}